import React, { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMarketing, SMSTemplate } from "../../../context/MarketingContext";
import { Input } from "../../../components/ui/input2";
import { Button } from "../../../components/ui/Button";
import { SMSTemplateDetailsDrawer } from "../../../components/marketing/SMSTemplateDetailsDrawer";
import { TemplateListSkeleton } from "../../../components/marketing/TemplateListSkeleton";
import { useToast } from "../../../hooks/useToast";
import { formatApiErrors } from "../../../services/marketingTemplatesApi";

const SMSTemplatesListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { smsTemplates, smsTemplatesLoading, removeSMSTemplate } = useMarketing();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<SMSTemplate | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(
    () =>
      smsTemplates.filter((t) => {
        const term = search.toLowerCase();
        return (
          t.name.toLowerCase().includes(term) ||
          t.text.toLowerCase().includes(term)
        );
      }),
    [smsTemplates, search],
  );

  const handleRowClick = (template: SMSTemplate) => {
    setSelected(template);
    setDrawerOpen(true);
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base lg:text-lg font-semibold text-gray-900">
              SMS Templates
            </h2>
            <p className="text-xs lg:text-sm text-gray-500">
              Define short SMS messages for reminders and campaigns.
            </p>
          </div>
          <Button
            onClick={() => navigate("/marketing/sms-templates/create")}
            className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New SMS Template
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SMS templates..."
              className="pl-9"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {smsTemplatesLoading ? (
            <div className="p-8 text-center text-sm text-gray-500">
              Loading SMS templates…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              No SMS templates yet. Click{" "}
              <span className="font-semibold text-[#885ABB]">New SMS Template</span>{" "}
              to create your first one.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleRowClick(template)}
                  className="w-full text-left px-4 lg:px-5 py-3 lg:py-3.5 hover:bg-purple-50/40 transition flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {template.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {template.text}
                    </p>
                  </div>
                  <span className="text-[11px] font-semibold text-[#885ABB] uppercase tracking-wide">
                    View
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <SMSTemplateDetailsDrawer
        isOpen={drawerOpen}
        template={selected}
        onClose={() => setDrawerOpen(false)}
        onEdit={(id) => navigate(`/marketing/sms-templates/${id}/edit`)}
        onDelete={async (id) => {
          try {
            await removeSMSTemplate(id);
            toast.success("SMS template deleted.");
            setSelected(null);
          } catch (err) {
            toast.error(formatApiErrors(err));
            throw err;
          }
        }}
      />
    </div>
  );
};

export default SMSTemplatesListPage;

